/**
 * `schemas/auth.ts` — `spec.md` §6.3 and §9.
 *
 * Three endpoints, one per step of the auth design: exchange `initData` for a session,
 * refresh that session, and mint the signed media URLs that are the only way a client
 * ever gets a playable `<audio src>` (§9.3: no cookies anywhere, so the credential is
 * in the URL).
 */
import * as z from 'zod';
import { NullableText, SessionToken, StartParam, TrackId, UnixSeconds, UserId } from './common.js';
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/auth/telegram
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * `Telegram.WebApp.platform`. Left as a bounded free string rather than an enum on
 * purpose: `users.platform` is documented as "ios|android|macos|…" and Telegram has
 * added platform strings before. A closed enum here would reject a launch from a client
 * that is otherwise perfectly valid.
 */
export const Platform = z.string().min(1).max(32);
/**
 * The raw `initData` querystring, verbatim. It must NOT be re-encoded or reordered
 * before it reaches the server: §9.1's data-check-string is computed over the fields as
 * received, and a helpful normalisation on the way in breaks the HMAC.
 */
export const InitDataString = z.string().min(1).max(8192);
export const AuthTelegramRequestSchema = z.strictObject({
    initData: InitDataString,
    platform: Platform.optional(),
});
/**
 * The authenticated identity. `firstName` is `z.string()` rather than `NonEmptyText`
 * because `users.first_name` defaults to `''` in the DDL and Telegram does permit an
 * empty first name; `username` and `languageCode` are nullable because a user may have
 * neither.
 */
export const SessionUserSchema = z.strictObject({
    id: UserId,
    firstName: z.string().max(256),
    username: NullableText,
    isAdmin: z.boolean(),
    languageCode: NullableText,
    isPremium: z.boolean(),
});
/**
 * Runtime facts the client needs at boot and cannot infer.
 *
 * `fts5` is here for the same reason `/healthz` reports it: whether search works is a
 * property of the SQLite build the process actually loaded, not of the Node version
 * (`api-groundtruth.md` §4).
 */
export const ServerInfoSchema = z.strictObject({
    indexReady: z.boolean(),
    trackCount: z.int().nonnegative(),
    fts5: z.boolean(),
});
/** The fields common to both the exchange and the refresh response. */
export const SessionSchema = z.strictObject({
    token: SessionToken,
    expiresAt: UnixSeconds,
    user: SessionUserSchema,
    server: ServerInfoSchema,
});
/**
 * `POST /api/auth/telegram` response. Adds `startParam`: the `startapp` payload of the
 * launch, or `null` when the Mini App was opened without a deep link.
 */
export const AuthTelegramResponseSchema = SessionSchema.extend({
    startParam: StartParam.nullable(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/auth/refresh
 *
 * Parameterless: the still-valid session token in the `Authorization` header IS the
 * request. §6.3 — "Response identical to above minus `startParam`."
 * ──────────────────────────────────────────────────────────────────────────── */
export const AuthRefreshResponseSchema = SessionSchema;
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/auth/media-token
 * ──────────────────────────────────────────────────────────────────────────── */
export const MEDIA_TOKEN_KINDS = ['audio', 'cover'];
export const MediaTokenKind = z.enum(MEDIA_TOKEN_KINDS);
/** 1..50 track ids per §6.3. Batching is what keeps the URL byte-stable (§9.4). */
export const MediaTokenRequestSchema = z.strictObject({
    trackIds: z.array(TrackId).min(1).max(50),
    kind: MediaTokenKind,
});
/**
 * `urls` is keyed by the track id **as a string**, and an unknown or deleted id is
 * **omitted rather than nulled** (§6.3). The key schema enforces the decimal-string
 * form so a route cannot accidentally key by shortId.
 */
export const MediaTokenResponseSchema = z.strictObject({
    ttl: z.int().positive(),
    expiresAt: UnixSeconds,
    urls: z.record(z.string().regex(/^\d{1,15}$/, { error: 'url map keys are decimal track ids' }), z.string().min(1).max(2048)),
});
//# sourceMappingURL=auth.js.map